import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { LvrHeading } from '../../../core/components/heading/heading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { PositionShare, User } from '../../../api/app-types/user.app';
import { Gender } from '../../../api/app-types/gender.app';
import { AdminUserView } from '../admin-user/admin-user-view/admin-user-view';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { USER_SERVICE } from '../../../api/services/user.service';
import { PositionShareList } from '../admin-user/position-share-list/position-share-list';
import { POSITION_SHARE_SERVICE } from '../../../api/services/position-share.service';
import { createPositionShareReadAccess } from '../../../shared/permissions/position-share-access.helper';
import { VALIDATION_SERVICE } from '../../../api/services/validation.service';
import { PositionShareEdit } from '../admin-user/position-share-edit/position-share-edit';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { RoleId } from '../../../api/app-types/role.app';

@Component({
	selector: 'lvr-specialist-details',
	imports: [LvrHeading, TranslocoPipe, LvrButton, AdminUserView, PositionShareList, PositionShareEdit],
	templateUrl: './specialist-details.html',
})
export class SpecialistDetails extends RouteInjection {
	// TODO: Missing proper distinction between admin-user and user views.
	// To be defined in upcoming PBIs and either unified or properly separated.
	private readonly session = inject(SessionManagerService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly userService = inject(USER_SERVICE);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly positionShareService = inject(POSITION_SHARE_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);

	private readonly userId = this.resolveRouteParamAsSignal('id');
	private readonly lastTitle = signal<string | null>(null);
	private readonly currentUserObjectId = signal<string | undefined>(undefined);
	private readonly currentUser = signal<User | undefined>(undefined);
	public readonly user = this.resolveDataAsSignal<User>('user');
	private readonly userOverride = signal<User | undefined>(undefined);
	public readonly viewUser = computed(() => this.userOverride() ?? this.user());

	private readonly positionShareReadAccess = createPositionShareReadAccess({
		userPermissions: this.session.permissions,
		viewedUser: this.user,
		currentUser: this.currentUser,
		currentUserObjectId: this.currentUserObjectId,
	});
	public readonly genders = this.resolveDataAsSignal<Gender[]>('genders');
	public readonly canTransferAssignments = signal(false);
	private readonly canReassignAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Reassign');
	public readonly canReadAllPositionShares = this.positionShareReadAccess.canReadAllPositionShares;
	public readonly canReadOwnPositionShares = this.positionShareReadAccess.canReadOwnPositionShares;
	public readonly isOwnUserDetail = this.positionShareReadAccess.isOwnUserDetail;
	public readonly canReadPositionShareDetails = this.positionShareReadAccess.canReadPositionShares;
	public readonly canReadPositionShares = this.canReadPositionShareDetails;
	public readonly canEditPositionShares = hasPermission(this.session.permissions, 'Users.PositionShares.Edit');
	public readonly canOpenUserSettings = hasPermission(this.session.permissions, ['Users.Edit']);
	public readonly canAssignClients = () => this.canReassignAssignmentsPermission();
	private readonly nonAssignableRoles = new Set<RoleId>([
		RoleId.IFD_TEAM_LEADER,
		RoleId.IFD_COORDINATION,
		RoleId.ADMIN,
		RoleId.SECRETARY,
	]);

	private readonly canReceiveTransferredAssignments = computed(() => {
		const roleId = this.user()?.roles?.id as RoleId;
		if (!roleId) return;
		return !this.nonAssignableRoles.has(roleId);
	});

	public readonly showTransferButton = computed(
		() => this.canReceiveTransferredAssignments() && this.canAssignClients() && this.canTransferAssignments(),
	);

	// We don’t have a single item GET yet, so we pass the full PositionShare via signal instead of routing.
	// We set the state in ?editShare=<id> so Back just closes the editor rather than leaving the page.
	public editingShare = signal<PositionShare | undefined>(undefined);

	public readonly userGender = computed(() => {
		const user = this.user();
		const genders = this.genders() ?? [];
		if (!user || !user.genderId) return '-';
		return genders.find((gender) => gender.id === user.genderId)?.salutation ?? '-';
	});
	private readonly recordTitle = computed(() => {
		const user = this.user();
		if (!user) return 'Fachkraft';
		const lastName = user.lastName?.trim() ?? '';
		const firstName = user.firstName?.trim() ?? '';
		if (lastName && firstName) return `${lastName}, ${firstName}`;
		return lastName || firstName || 'Fachkraft';
	});

	public constructor() {
		super();
		void this.loadCurrentUserObjectId();
		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});

		effect(() => {
			const userId = this.user()?.id;
			if (!userId) {
				this.canTransferAssignments.set(false);
				return;
			}
			this.validationApi.validateMoveAction(userId).then((canTransfer) => {
				this.canTransferAssignments.set(canTransfer);
			});
		});
	}

	private async loadCurrentUserObjectId() {
		try {
			const me = await this.userService.getSingleMe();
			this.currentUserObjectId.set(me.objectId);
			this.currentUser.set(me);
		} catch {
			this.currentUserObjectId.set(undefined);
			this.currentUser.set(undefined);
		}
	}

	public openMove() {
		this.router.navigate(['/desk/user', this.userId(), 'assignments', 'move']);
	}

	/**
	 * Opens the Position Share editor and sets the state in ?editShare=<id>.
	 **/
	public onEditShare = (share: PositionShare) => {
		if (!this.canEditPositionShares()) return;
		this.editingShare.set(share);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { editShare: share.id },
			queryParamsHandling: 'merge',
			replaceUrl: false, // for closing it with 'back'
		});
	};

	public openEdit() {
		this.router.navigate(['/admin/users/', this.userId(), 'edit']);
	}

	/**
	 * Handles the position share changes save event.
	 * Updates the position share, the user panel, closes the editor, and clears ?editShare.
	 */
	public onShareSaved = async (share: PositionShare) => {
		if (!this.canEditPositionShares()) return;
		try {
			await this.positionShareService.update(this.editingShare()!.id, share);
			const updated = await this.userService.getSingle(this.viewUser()!.objectId);
			this.userOverride.set(updated);

			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { editShare: null },
				queryParamsHandling: 'merge',
				replaceUrl: false,
			});
			this.editingShare.set(undefined);
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.changes_saved')),
			);
		} catch {
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.changes_save_error')),
			);
		}
	};

	/**
	 * Cancels the position share edit mode without saving changes.
	 */
	public onShareCancel = () => {
		this.editingShare.set(undefined);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { editShare: null },
			queryParamsHandling: 'merge',
			replaceUrl: false,
		});
	};
}
